/* eslint-disable max-lines-per-function */
import { Button, Calendar, DatePickerInput } from '@/components';
import {
  FieldBase,
  FieldProps,
  useDatePickerField,
  useFieldContext,
} from '@/components/form';
import {
  Drawer,
  DrawerClose,
  DrawerContent,
  DrawerDescription,
  DrawerTitle,
  DrawerTrigger,
} from '@/components/ui/drawer';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import { YearMonthPickerDrawer } from '@/components/ui/year-month-picker-drawer';
import { getMonthNameByDate } from '@/utils';
import {
  faCalendar,
  faChevronLeft,
  faChevronRight,
} from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Portal } from '@radix-ui/react-portal';
import dayjs from 'dayjs';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

type DatePickerFieldProps = FieldProps;

function formatDate(date?: dayjs.Dayjs) {
  if (!date) return '';
  return date.locale('de').format('dd., D. MMMM YYYY');
}

export function FieldDatePicker({
  label,
  isRequired,
  description,
  className,
  disabled,
  ...props
}: DatePickerFieldProps) {
  const field = useFieldContext<string | undefined>();
  const { t } = useTranslation();

  const dateValue = field.state.value ? dayjs(field.state.value) : undefined;
  const drawer = useDatePickerField(field.state.value);

  const [popoverOpen, setPopoverOpen] = useState(false);

  const handleSelect = (date: Date | undefined) => {
    if (date) {
      drawer.setTempDate(date);
      field.handleChange(dayjs(date).toISOString());
      field.handleBlur();
      setPopoverOpen(false);
    }
  };

  return (
    <FieldBase
      label={label}
      isRequired={isRequired}
      description={description}
      className={className}
      {...props}>
      <Drawer open={drawer.open} onOpenChange={drawer.handleOpenChange}>
        <DrawerTrigger asChild>
          <div className="flex md:hidden">
            <DatePickerInput
              endIcon={faCalendar}
              className="cursor-pointer text-left "
              value={drawer.tempDate ? formatDate(dayjs(drawer.tempDate)) : ''}
              placeholder={t('general.date.datepicker.placeholder')}
              readOnly
              disabled={disabled}
            />
          </div>
        </DrawerTrigger>
        <DrawerContent>
          <DrawerTitle className="sr-only">{label}</DrawerTitle>
          <DrawerDescription className="sr-only">{label}</DrawerDescription>
          <div className="w-full p-4" data-vaul-no-drag>
            <div className="flex items-center justify-between mb-4">
              <Button
                variant="ghost"
                size="icon"
                onClick={drawer.handlePreviousMonth}
                className="h-7 w-7"
                disabled={disabled}>
                <FontAwesomeIcon icon={faChevronLeft} className="h-4 w-4" />
              </Button>
              <YearMonthPickerDrawer
                year={dayjs(drawer.displayMonth).year()}
                month={dayjs(drawer.displayMonth).month() + 1}
                onSelect={drawer.handleYearMonthSelect}
                confirmLabel={t('general.date.datepicker.submit')}>
                <Button
                  variant="ghost"
                  className="hover:bg-neutrals-100"
                  disabled={disabled}>
                  <span className="font-semibold text-sm">
                    {getMonthNameByDate(
                      dayjs(drawer.displayMonth).format('YYYYMM'),
                    )}
                  </span>
                </Button>
              </YearMonthPickerDrawer>
              <Button
                variant="ghost"
                size="icon"
                onClick={drawer.handleNextMonth}
                className="h-7 w-7"
                disabled={disabled}>
                <FontAwesomeIcon icon={faChevronRight} className="h-4 w-4" />
              </Button>
            </div>
            <Calendar
              mode="single"
              selected={drawer.tempDate}
              onSelect={disabled ? undefined : drawer.handleDateSelect}
              month={drawer.displayMonth}
              classNames={{
                months: 'flex flex-col space-y-4',
                month: 'space-y-4',
                caption: 'hidden',
                nav: 'hidden',
                head_cell:
                  'text-neutrals-500 h-9 w-full font-normal text-[0.8rem]',
                cell: 'h-9 w-full text-center text-sm p-0 relative',
                day: 'h-9 w-9 p-0 font-normal aria-selected:opacity-100 rounded-md',
                day_today:
                  'bg-slate-200 text-slate-900 aria-selected:bg-primary-blue-600 aria-selected:text-white',
                day_selected:
                  'bg-primary-blue-600 text-white hover:bg-primary-blue-600 hover:text-white focus:bg-primary-blue-600 focus:text-white',
              }}
              disabled={disabled}
            />
          </div>
          <div className="px-4 pb-4 flex flex-col gap-3">
            <Button
              className="w-full"
              variant="ghost"
              onClick={drawer.handleTodaySelect}
              disabled={disabled}>
              {t('general.date.today')}
            </Button>
            <DrawerClose asChild>
              <Button
                className="w-full"
                size="lg"
                onClick={() =>
                  drawer.handleConfirm(field.handleChange, field.handleBlur)
                }
                disabled={disabled}>
                {t('general.date.datepicker.submit')}
              </Button>
            </DrawerClose>
          </div>
        </DrawerContent>
      </Drawer>

      <Popover open={popoverOpen} onOpenChange={setPopoverOpen}>
        <PopoverTrigger asChild>
          <div className="hidden md:flex">
            <DatePickerInput
              endIcon={faCalendar}
              className="cursor-pointer h-10 text-start"
              value={dateValue ? formatDate(dateValue) : ''}
              placeholder={t('general.date.datepicker.placeholder')}
              readOnly
              disabled={disabled}
            />
          </div>
        </PopoverTrigger>
        <Portal>
          <PopoverContent
            className="w-fit flex flex-col bg-white z-50"
            align="start">
            <Calendar
              mode="single"
              selected={dateValue?.toDate()}
              onSelect={disabled ? undefined : handleSelect}
              defaultMonth={dateValue?.toDate()}
              classNames={{
                months: 'flex flex-col space-y-4',
                month: 'space-y-4',
                head_cell: 'text-neutrals-700 w-full font-normal text-[0.8rem]',
                day_today:
                  'bg-slate-200 text-slate-900 aria-selected:bg-primary-blue-600 aria-selected:text-white',
                day_selected:
                  'bg-primary-blue-600 text-white hover:bg-primary-blue-600 hover:text-white focus:bg-primary-blue-600 focus:text-white',
              }}
              disabled={disabled}
            />
          </PopoverContent>
        </Portal>
      </Popover>
    </FieldBase>
  );
}
